import { UserCompanyDto } from '../../services/api.types'

/**
 * Checks if a specific feature flag exists in any of the user's company scopes
 *
 * @param userCompanies Array of user companies
 * @param featureFlag Feature flag name to check for
 * @returns True if the feature flag is found in any company scope, false otherwise
 */
export const hasFeatureFlag = (userCompanies: UserCompanyDto[] | null | undefined, featureFlag: string): boolean => {
  if (!userCompanies || userCompanies.length === 0) {
    return false
  }

  return userCompanies.some((uc) => uc.scope?.includes(featureFlag)) ?? false
}

/**
 * Checks if a specific feature flag exists in the scope of a specific company
 *
 * @param userCompanies Array of user companies
 * @param companyId The company ID to check the feature flag for
 * @param featureFlag Feature flag name to check for
 * @returns True if the feature flag is found in the specified company's scope, false otherwise
 */
export const hasCompanyFeatureFlag = (
  userCompanies: UserCompanyDto[] | null | undefined,
  companyId: string | null | undefined,
  featureFlag: string,
): boolean => {
  if (!userCompanies || userCompanies.length === 0 || !companyId) {
    return false
  }

  const company = userCompanies.find((uc) => uc.companyId === companyId)
  return company?.scope?.includes(featureFlag) ?? false
}

/**
 * Converts a SCREAMING_SNAKE_CASE feature flag key to its legacy camelCase scope name.
 * Must stay in sync with Feature.toLegacyScopeName() on the backend.
 */
const LEGACY_OVERRIDES: Record<string, string> = {
  HIDE_SETTINGS_ACTIVITY_INDEX_CTA: 'hideSettingsActivityIndexCTA',
}

function toCamelCase(screaming: string): string {
  const override = LEGACY_OVERRIDES[screaming]
  if (override) return override
  let result = ''
  let capitalizeNext = false
  for (let i = 0; i < screaming.length; i++) {
    const c = screaming[i]
    if (c === '_') {
      capitalizeNext = true
    } else if (capitalizeNext) {
      result += c.toUpperCase()
      capitalizeNext = false
    } else {
      result += c.toLowerCase()
    }
  }
  return result
}

/**
 * Builds a scope string from feature flags, matching the backend's computeLegacyScopeString format.
 */
export const buildScopeFromFeatures = (features: { key: string; enabled: boolean }[]): string => {
  return features
    .filter((f) => f.enabled)
    .map((f) => toCamelCase(f.key))
    .join(' ')
}
